import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { CommercialProduct, Id, RetentionOpportunity, RetentionOpportunityType, StaffUser } from "../types";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useDrawer } from "../context/DrawerContext";
import { formatDateTime } from "../lib/format";

const TYPES: { key: RetentionOpportunityType | "ALL"; label: string }[] = [
  { key: "ALL", label: "Todas" },
  { key: "SAME_SERVICE_REBUY", label: "Recompra mismo servicio" },
  { key: "CROSS_SELL", label: "Venta cruzada" },
  { key: "UPSELL", label: "Upsell" },
  { key: "MAINTENANCE", label: "Mantenimiento" },
  { key: "REACTIVATION", label: "Reactivación" },
];

const SELLER_ROLES = ["admin", "director", "sales_manager", "sales"];
const ASSIGN_ROLES = ["admin", "director", "sales_manager"];

function statusPillClass(status: string) {
  if (status === "WON") return "pill-ok";
  if (status === "LOST" || status === "EXPIRED") return "pill-bad";
  if (status === "ASSIGNED") return "pill-warn";
  return "";
}

export default function Retencion() {
  const { user } = useAuth();
  const { show } = useToast();
  const { open } = useDrawer();

  const [typeFilter, setTypeFilter] = useState<RetentionOpportunityType | "ALL">("ALL");
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [opportunities, setOpportunities] = useState<RetentionOpportunity[]>([]);
  const [products, setProducts] = useState<CommercialProduct[]>([]);
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<Id | null>(null);
  const [sellerChoice, setSellerChoice] = useState<Record<string, string>>({});
  const [closeInputs, setCloseInputs] = useState<Record<string, { resulting_opportunity_id: string; resulting_sale_id: string }>>({});

  const canAssign = !!user && ASSIGN_ROLES.includes(user.role_code);
  const canClose = !!user && SELLER_ROLES.includes(user.role_code);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, prods, staff] = await Promise.all([
        api.getRetentionOpportunities({
          opportunity_type: typeFilter === "ALL" ? undefined : typeFilter,
          upcoming: showUpcoming,
        }),
        api.getCommercialProducts().catch(() => []),
        api.users().catch(() => []),
      ]);
      setOpportunities(rows);
      setProducts(prods);
      setUsers(staff);
    } catch (e) {
      if (e instanceof ApiClientError) setError(e.message);
      else setError("No se pudieron cargar las oportunidades de retención.");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, showUpcoming]);

  useEffect(() => {
    load();
  }, [load]);

  function productName(id: Id | null | undefined) {
    if (!id) return "-";
    return products.find((p) => String(p.id) === String(id))?.name || `#${id}`;
  }
  function userName(id: Id | null | undefined) {
    if (!id) return "Sin asignar";
    return users.find((u) => String(u.id) === String(id))?.full_name || `#${id}`;
  }

  async function assign(opp: RetentionOpportunity) {
    const sellerId = sellerChoice[String(opp.id)];
    if (!sellerId) {
      show("Elegí una vendedora/vendedor.", "error");
      return;
    }
    setBusyId(opp.id);
    try {
      await api.assignRetentionOpportunity(opp.id, { seller_id: sellerId });
      show("Oportunidad asignada.", "success");
      load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo asignar.", "error");
    } finally {
      setBusyId(null);
    }
  }

  async function close(opp: RetentionOpportunity, status: "WON" | "LOST") {
    setBusyId(opp.id);
    try {
      const inputs = closeInputs[String(opp.id)] || { resulting_opportunity_id: "", resulting_sale_id: "" };
      await api.closeRetentionOpportunity(opp.id, {
        status,
        resulting_opportunity_id: status === "WON" && inputs.resulting_opportunity_id ? inputs.resulting_opportunity_id : undefined,
        resulting_sale_id: status === "WON" && inputs.resulting_sale_id ? inputs.resulting_sale_id : undefined,
      });
      show(status === "WON" ? "Oportunidad cerrada como ganada." : "Oportunidad cerrada como perdida.", "success");
      load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo cerrar la oportunidad.", "error");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="tasks-page">
      <h1>Retención y reventa</h1>
      <p className="meta">
        Oportunidades de recompra, venta cruzada, upsell, mantenimiento y reactivación generadas automáticamente a
        partir de tratamientos realizados y ventanas de recurrencia.
      </p>

      <div className="pipeline-tabs">
        {TYPES.map((t) => (
          <button key={t.key} className={`tab ${typeFilter === t.key ? "active" : ""}`} onClick={() => setTypeFilter(t.key as any)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="row" style={{ marginTop: 8, gap: 8 }}>
        <label className="meta">
          <input type="checkbox" checked={showUpcoming} onChange={(e: any) => setShowUpcoming(e.target.checked)} /> Incluir futuras (todavía no
          elegibles)
        </label>
      </div>

      {loading && <div className="loading-strip">Cargando oportunidades...</div>}
      {error && <p className="form-error">{error}</p>}

      <table className="task-table">
        <thead>
          <tr>
            <th>Paciente</th>
            <th>Tipo</th>
            <th>Sugerido</th>
            <th>Elegible desde</th>
            <th>Prioridad IA</th>
            <th>Responsable</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {opportunities.map((opp) => {
            const eligible = new Date(opp.eligible_at).getTime() <= Date.now();
            return (
              <tr key={opp.id}>
                <td>
                  <button className="link-btn" onClick={() => open(opp.contact_id)}>
                    Paciente #{opp.contact_id}
                  </button>
                </td>
                <td>
                  <span className="pill">{opp.opportunity_type}</span>
                </td>
                <td>{productName(opp.suggested_product_id)}</td>
                <td className={!eligible ? "meta" : ""}>{formatDateTime(opp.eligible_at)} {!eligible && "(futura)"}</td>
                <td>{opp.ai_priority !== null && opp.ai_priority !== undefined ? Number(opp.ai_priority).toFixed(2) : "-"}</td>
                <td className="meta">{userName(opp.assigned_seller_id)}</td>
                <td>
                  <span className={`pill ${statusPillClass(opp.status)}`}>{opp.status}</span>
                </td>
                <td>
                  {!eligible ? (
                    <span className="meta">No elegible aún</span>
                  ) : opp.status === "WON" || opp.status === "LOST" || opp.status === "EXPIRED" ? (
                    <span className="meta">{opp.reason || "-"}</span>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {canAssign && opp.status === "OPEN" && (
                        <div className="row" style={{ gap: 6 }}>
                          <select
                            value={sellerChoice[String(opp.id)] || ""}
                            onChange={(e: any) => setSellerChoice((m) => ({ ...m, [String(opp.id)]: e.target.value }))}
                          >
                            <option value="">Asignar a...</option>
                            {users
                              .filter((u) => ["sales", "sales_manager"].includes(u.role_code))
                              .map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.full_name}
                                </option>
                              ))}
                          </select>
                          <button className="link-btn" disabled={busyId === opp.id} onClick={() => assign(opp)}>
                            Asignar
                          </button>
                        </div>
                      )}
                      {canClose && (
                        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                          <input
                            placeholder="ID presupuesto/oport. resultante"
                            style={{ width: 150 }}
                            value={closeInputs[String(opp.id)]?.resulting_opportunity_id || ""}
                            onChange={(e: any) =>
                              setCloseInputs((m) => ({
                                ...m,
                                [String(opp.id)]: { ...(m[String(opp.id)] || { resulting_opportunity_id: "", resulting_sale_id: "" }), resulting_opportunity_id: e.target.value },
                              }))
                            }
                          />
                          <button className="link-btn" disabled={busyId === opp.id} onClick={() => close(opp, "WON")}>
                            Ganada
                          </button>
                          <button className="link-btn" disabled={busyId === opp.id} onClick={() => close(opp, "LOST")}>
                            Perdida
                          </button>
                        </div>
                      )}
                      {canClose && (
                        <p className="meta" style={{ margin: 0, maxWidth: 260 }}>
                          Al cerrar como Ganada, el origen del presupuesto resultante queda fijado en "Paciente propio
                          (reventa)" — no es editable desde acá.
                        </p>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
          {!loading && opportunities.length === 0 && (
            <tr>
              <td colSpan={8} className="meta">
                No hay oportunidades de retención para este filtro.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
